import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { TIDAL_LEVELS } from "@nav-games/tides";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface LevelsProps {
  onNavigate: (to: string) => void;
}

/// Fixed pixel y-positions (in viewBox units) for each datum line.
/// Match the geometry used in TidalHeightsFigure so the drag-and-
/// drop game and the static reference render the same scale.
const LEVEL_Y: Record<string, number> = {
  HAT: 80,
  MHWS: 100,
  MHWN: 130,
  ML: 170,
  MLWN: 210,
  MLWS: 240,
  LAT: 255,
  CD: 270,
};

const ABBREVIATIONS = TIDAL_LEVELS.map((l) => l.abbreviation);

/// Levels game: a stripped-down tide cross-section with eight blank
/// drop targets at each datum line. The player drags labels from a
/// tray onto each line; each drop is scored against the answer the
/// instant it lands. First-attempt accuracy is recorded so repeated
/// plays surface which abbreviations are still confused.
export function TidalLevelsGame({ onNavigate }: LevelsProps) {
  const [tray, setTray] = useState<readonly string[]>(() =>
    shuffle(ABBREVIATIONS),
  );
  /// `placed[lineAbbr]` = the abbreviation currently dropped on
  /// that line. null = nothing placed yet.
  const [placed, setPlaced] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(ABBREVIATIONS.map((a) => [a, null])),
  );
  /// Counter of WRONG drops the player has made this session — used
  /// for the score reveal. Right-on-first-attempt = score++.
  const wrongDrops = useRef(0);
  const [helpOpen, setHelpOpen] = useState(false);

  const allCorrect = ABBREVIATIONS.every((a) => placed[a] === a);
  const allPlaced = ABBREVIATIONS.every((a) => placed[a] !== null);

  const handleDrop = useCallback(
    (lineAbbr: string, droppedAbbr: string) => {
      setPlaced((p) => ({ ...p, [lineAbbr]: droppedAbbr }));
      setTray((t) => t.filter((x) => x !== droppedAbbr));
      if (lineAbbr !== droppedAbbr) wrongDrops.current += 1;
    },
    [],
  );

  const handleReset = useCallback(() => {
    setTray(shuffle(ABBREVIATIONS));
    setPlaced(Object.fromEntries(ABBREVIATIONS.map((a) => [a, null])));
    wrongDrops.current = 0;
  }, []);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <h2 style={{ margin: 0 }}>Name the tidal levels</h2>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel
        section={helpOpen ? "tides" : null}
        onClose={() => setHelpOpen(false)}
      />
      <p className="muted">
        Drag each abbreviation from the tray on the right onto the matching
        horizontal datum line. The diagram is scaled exactly like the
        Reference cross-section.
      </p>

      <div className="card">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) 130px",
            gap: 12,
            alignItems: "start",
          }}
        >
          <Diagram placed={placed} onDrop={handleDrop} />
          <Tray items={tray} />
        </div>
        {allPlaced && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background: allCorrect
                ? "rgba(44, 122, 59, 0.10)"
                : "rgba(184, 48, 42, 0.10)",
            }}
          >
            {allCorrect ? (
              <>
                <p style={{ margin: "0 0 4px", fontWeight: 600 }}>
                  ✓ All eight datums placed correctly
                </p>
                <p className="small muted" style={{ margin: 0 }}>
                  {wrongDrops.current === 0
                    ? "First-attempt clean run — perfect score."
                    : `${wrongDrops.current} wrong drop${wrongDrops.current === 1 ? "" : "s"} along the way.`}
                </p>
              </>
            ) : (
              <p style={{ margin: 0, fontWeight: 600 }}>
                Not quite — labels still in the wrong places. Drag them off
                and try again.
              </p>
            )}
          </div>
        )}
        <div
          className="row"
          style={{
            gap: 8,
            marginTop: 12,
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          <button onClick={handleReset}>Reset board</button>
          <button
            className="secondary"
            onClick={() => onNavigate("/play/tides")}
          >
            ← Back to Tides
          </button>
        </div>
      </div>
    </>
  );
}

interface DiagramProps {
  placed: Record<string, string | null>;
  onDrop: (lineAbbr: string, droppedAbbr: string) => void;
}

/// Render the cross-section background + eight horizontal datum
/// lines, each with a drop target on its right edge. The drop
/// target is a regular HTML <div> overlaid on the SVG via absolute
/// positioning so HTML drag-and-drop events fire cleanly — SVG
/// elements as drop targets are inconsistent across browsers.
function Diagram({ placed, onDrop }: DiagramProps) {
  // 0..1 normalised y position for each line, matched to LEVEL_Y on
  // a 360-tall viewBox. Lets the overlay positions stay in sync if
  // we resize the figure later.
  const VB_H = 360;

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <svg
        viewBox="0 0 600 360"
        width="100%"
        style={{ display: "block" }}
        role="img"
        aria-label="Tide cross-section with eight unlabelled datum lines."
      >
        {/* Sky */}
        <rect x="0" y="0" width="600" height={LEVEL_Y["MHWS"]! - 6} fill="#dbeafe" />
        {/* Sea */}
        <rect
          x="0"
          y={LEVEL_Y["MHWS"]! - 6}
          width="600"
          height={VB_H - (LEVEL_Y["MHWS"]! - 6)}
          fill="#9cc3e0"
          opacity="0.6"
        />
        {/* Eight horizontal datum lines */}
        {ABBREVIATIONS.map((a) => {
          const y = LEVEL_Y[a]!;
          const isCd = a === "CD";
          return (
            <g key={a}>
              <line
                x1="0"
                y1={y}
                x2="500"
                y2={y}
                stroke="#0f172a"
                strokeWidth={isCd ? 1.6 : 0.6}
                strokeDasharray={a === "HAT" || a === "LAT" ? "4 4" : undefined}
              />
              {isCd && (
                <line
                  x1="0"
                  y1={y + 2}
                  x2="500"
                  y2={y + 2}
                  stroke="#0f172a"
                  strokeWidth={1.6}
                />
              )}
            </g>
          );
        })}
      </svg>
      {/* HTML overlay — one drop target per line, anchored to the
          right edge of the SVG. Uses percentage positioning so the
          targets rescale with the figure. */}
      {ABBREVIATIONS.map((a) => {
        const y = LEVEL_Y[a]!;
        const placedAbbr = placed[a] ?? null;
        const correct = placedAbbr === a;
        return (
          <DropTarget
            key={a}
            // Centre the target vertically on the datum line.
            top={`calc(${(y / VB_H) * 100}% - 14px)`}
            placed={placedAbbr}
            correct={correct}
            onDrop={(droppedAbbr) => onDrop(a, droppedAbbr)}
          />
        );
      })}
    </div>
  );
}

function DropTarget({
  top,
  placed,
  correct,
  onDrop,
}: {
  top: string;
  placed: string | null;
  correct: boolean;
  onDrop: (abbr: string) => void;
}) {
  const [hover, setHover] = useState(false);
  const handlers = {
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      setHover(true);
    },
    onDragLeave: () => setHover(false),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setHover(false);
      const abbr = e.dataTransfer.getData("text/plain");
      if (abbr) onDrop(abbr);
    },
  };
  // Filled slot — show the placed pill, colour by correct/incorrect.
  if (placed) {
    return (
      <div
        {...handlers}
        style={{
          position: "absolute",
          right: 8,
          top,
          width: 80,
          height: 28,
          borderRadius: 6,
          padding: "4px 8px",
          fontWeight: 700,
          fontSize: 13,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: correct
            ? "rgba(44, 122, 59, 0.22)"
            : "rgba(184, 48, 42, 0.22)",
          color: correct ? "#1f5e2c" : "#7a1f1c",
          border: `1.5px solid ${correct ? "#2c7a3b" : "#b8302a"}`,
        }}
      >
        {placed}
      </div>
    );
  }
  return (
    <div
      {...handlers}
      style={{
        position: "absolute",
        right: 8,
        top,
        width: 80,
        height: 28,
        borderRadius: 6,
        border: hover ? "2px dashed #1e6091" : "2px dashed #94a3b8",
        background: hover ? "rgba(30, 96, 145, 0.10)" : "transparent",
      }}
    />
  );
}

function Tray({ items }: { items: readonly string[] }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: 8,
        background: "var(--accent-soft, #f5f1e6)",
        borderRadius: 6,
      }}
    >
      <div className="small muted" style={{ marginBottom: 4, fontWeight: 600 }}>
        Tray
      </div>
      {items.length === 0 && (
        <div className="small muted">All placed.</div>
      )}
      {items.map((abbr) => (
        <Pill key={abbr} abbr={abbr} />
      ))}
    </div>
  );
}

function Pill({ abbr }: { abbr: string }) {
  const onDragStart = useCallback(
    (e: React.DragEvent) => {
      e.dataTransfer.setData("text/plain", abbr);
      e.dataTransfer.effectAllowed = "move";
    },
    [abbr],
  );
  // Touch fallback: long-press to "pick up", then tap a drop target.
  // The TouchPill child handles the touchmove → re-fire drop event.
  return (
    <TouchPill abbr={abbr}>
      <div
        draggable
        onDragStart={onDragStart}
        style={{
          padding: "6px 10px",
          background: "#fff",
          border: "1.5px solid #475569",
          borderRadius: 6,
          cursor: "grab",
          fontWeight: 700,
          fontSize: 13,
          textAlign: "center",
          userSelect: "none",
          touchAction: "none",
        }}
      >
        {abbr}
      </div>
    </TouchPill>
  );
}

/// Touch/pointer fallback for HTML5 drag-and-drop. iOS Safari does
/// NOT fire the native drag events for touch, so we synthesise a
/// drag by:
///   - capturing the pointer on touchstart
///   - tracking pointer position with a fixed-position floating
///     clone follower
///   - on release, hit-testing `document.elementFromPoint` against
///     the drop targets and firing a synthetic `drop` event so the
///     same handler path works for mouse and touch.
function TouchPill({
  abbr,
  children,
}: {
  abbr: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onPointerDown = (ev: PointerEvent) => {
      if (ev.pointerType === "mouse") return; // let native HTML5 D&D handle mouse
      ev.preventDefault();
      // Build a floating clone that follows the finger.
      const clone = document.createElement("div");
      clone.textContent = abbr;
      Object.assign(clone.style, {
        position: "fixed",
        zIndex: "9999",
        padding: "6px 10px",
        background: "#fff",
        border: "1.5px solid #1e6091",
        borderRadius: "6px",
        fontWeight: "700",
        fontSize: "13px",
        pointerEvents: "none",
        opacity: "0.92",
        boxShadow: "0 4px 10px rgba(0,0,0,0.18)",
      } as CSSStyleDeclaration);
      document.body.appendChild(clone);
      dragging.current = clone;
      el.setPointerCapture(ev.pointerId);
    };

    const onPointerMove = (ev: PointerEvent) => {
      const clone = dragging.current;
      if (!clone) return;
      clone.style.left = `${ev.clientX - 30}px`;
      clone.style.top = `${ev.clientY - 16}px`;
    };

    const onPointerUp = (ev: PointerEvent) => {
      const clone = dragging.current;
      if (!clone) return;
      dragging.current = null;
      clone.remove();
      // Hit-test the underlying element.
      const target = document.elementFromPoint(ev.clientX, ev.clientY);
      // Walk up until we find a drop target (style border:dashed or
      // the placed-pill border). Easier: dispatch a synthetic drop
      // event on whichever element is under the pointer.
      if (target) {
        const dt = new DataTransfer();
        dt.setData("text/plain", abbr);
        const dropEv = new DragEvent("drop", {
          bubbles: true,
          cancelable: true,
          dataTransfer: dt,
        });
        target.dispatchEvent(dropEv);
      }
      el.releasePointerCapture(ev.pointerId);
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
    };
  }, [abbr]);

  return <div ref={ref}>{children}</div>;
}

function shuffle<T>(input: readonly T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = arr[i]!;
    const aj = arr[j]!;
    arr[i] = aj;
    arr[j] = ai;
  }
  return arr;
}
